<template>
  <div class="sn-root" :style="themePageStyle">
  <head>
    <meta charset="utf-8" /><title>FLOCKR | Supplier Network</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="/assets/images/favicon.svg">
    <link href="/assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="/assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="/assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="/assets/css/custom.css" rel="stylesheet" type="text/css" />
  </head>
  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <nav class="sn-topbar">
      <div class="container d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center" style="gap:16px;">
          <back-button class="me-2" />
          <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
          <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;"><i class="mdi mdi-account-group me-1"></i>Supplier Network</span>
        </div>
        <button class="btn btn-sm btn-warning" @click="showAddModal=true"><i class="mdi mdi-plus me-1"></i>Add Supplier</button>
      </div>
    </nav>

    <div class="container sn-container">
      <!-- Stats -->
      <div class="row g-3 mb-4">
        <div class="col-4">
          <div class="sn-stat"><div class="sn-stat-val">{{ suppliers.length }}</div><div class="sn-stat-lbl">Total Suppliers</div></div>
        </div>
        <div class="col-4">
          <div class="sn-stat"><div class="sn-stat-val" style="color:#50C878;">{{ countTrust('verified') }}</div><div class="sn-stat-lbl">Verified</div></div>
        </div>
        <div class="col-4">
          <div class="sn-stat"><div class="sn-stat-val" style="color:#f1b44c;">{{ countTrust('trusted') }}</div><div class="sn-stat-lbl">Trusted</div></div>
        </div>
      </div>

      <!-- Search & Filter -->
      <div class="d-flex gap-2 mb-3">
        <input v-model="searchQuery" type="text" class="form-control sn-input" placeholder="Search suppliers..." />
        <select v-model="trustFilter" class="form-select sn-input" style="max-width:160px;">
          <option value="">All Levels</option>
          <option value="new">New</option>
          <option value="trusted">Trusted</option>
          <option value="verified">Verified</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

      <div v-else-if="filtered.length === 0" class="text-center py-5">
        <i class="mdi mdi-account-group-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
        <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">{{ suppliers.length === 0 ? 'No Suppliers Yet' : 'No Matching Suppliers' }}</h5>
        <p style="color:var(--fm-s,#6B6356);">{{ suppliers.length === 0 ? 'Build your supplier network by adding trusted sellers.' : 'Try a different search.' }}</p>
        <button v-if="suppliers.length === 0" class="btn btn-warning mt-2" @click="showAddModal=true"><i class="mdi mdi-plus me-1"></i>Add First Supplier</button>
      </div>

      <div v-else>
        <div v-for="(s,si) in filtered" :key="'s-'+si" class="sn-card">
          <div class="d-flex align-items-center gap-3">
            <div class="sn-avatar">
              <img v-if="s.supplier_avatar" :src="photoUrl(s.supplier_avatar)" />
              <span v-else>{{ getInitials(s.supplier_name) }}</span>
            </div>
            <div class="flex-fill">
              <div class="d-flex align-items-center gap-2">
                <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">{{ s.nickname || s.supplier_name }}</span>
                <span class="sn-trust" :class="'tl-'+s.trust_level">{{ s.trust_level }}</span>
              </div>
              <div v-if="s.nickname && s.supplier_name !== s.nickname" style="color:var(--fm-s,#6B6356);font-size:11px;">{{ s.supplier_name }}</div>
              <div style="color:var(--fm-m,#A09888);font-size:12px;">
                <span v-if="s.county_name"><i class="mdi mdi-map-marker me-1"></i>{{ s.county_name }}</span>
                <span v-if="s.supplier_phone" class="ms-2"><i class="mdi mdi-phone me-1"></i>{{ s.supplier_phone }}</span>
              </div>
              <div v-if="s.total_orders" style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:2px;">{{ s.total_orders }} orders · {{ (s.currency || 'KES') }} {{ formatPrice(s.total_value) }} total</div>
            </div>
            <div class="d-flex gap-1">
              <button v-if="s.supplier_phone" class="btn btn-sm btn-outline-success" @click.stop="callSupplier(s)"><i class="mdi mdi-phone"></i></button>
              <button class="btn btn-sm btn-outline-warning" @click.stop="editSupplier(s)"><i class="mdi mdi-pencil"></i></button>
              <button class="btn btn-sm btn-outline-danger" @click.stop="removeSupplier(s)"><i class="mdi mdi-delete"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Supplier Modal -->
    <div v-if="showAddModal" class="sn-modal-overlay" @click.self="showAddModal=false">
      <div class="sn-modal">
        <h6 class="fw-bold mb-3" style="color:var(--fm-h,#F5F0E8);">Add Supplier</h6>
        <div class="mb-2">
          <label class="sn-label">Supplier ID <span class="text-danger">*</span></label>
          <input v-model="addForm.supplierId" type="text" class="form-control sn-input" placeholder="Enter supplier's user ID" />
        </div>
        <div class="mb-2">
          <label class="sn-label">Nickname</label>
          <input v-model="addForm.nickname" type="text" class="form-control sn-input" placeholder="Optional nickname" />
        </div>
        <div class="mb-3">
          <label class="sn-label">Trust Level</label>
          <select v-model="addForm.trustLevel" class="form-select sn-input">
            <option value="new">New</option>
            <option value="trusted">Trusted</option>
            <option value="verified">Verified</option>
            <option value="premium">Premium</option>
          </select>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary flex-fill" @click="showAddModal=false">Cancel</button>
          <button class="btn btn-warning flex-fill" @click="addSupplier" :disabled="!addForm.supplierId">Add</button>
        </div>
      </div>
    </div>

  </div>
  </body>
  </div>
</template>
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style scoped>
.sn-root{background:var(--fm-bg2,#0F0F0F)!important;min-height:100vh}
.sn-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:rgba(10,10,10,.95)!important;backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.sn-container{padding-top:80px;padding-bottom:60px;max-width:900px}
.sn-stat{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:16px;text-align:center}
.sn-stat-val{font-size:22px;font-weight:700;color:var(--fm-h,#F5F0E8)!important}
.sn-stat-lbl{font-size:11px;color:var(--fm-s,#6B6356)!important;margin-top:2px}
.sn-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#2A2520);color:var(--fm-h,#F5F0E8)!important;border-radius:8px}
.sn-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15);background:var(--fm-bg2,#0F0F0F)!important;color:var(--fm-h,#F5F0E8)!important}
.sn-input option{background:var(--fm-bg1,#141414)!important;color:var(--fm-h,#F5F0E8)!important}
.sn-card{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:16px;margin-bottom:10px;transition:all .2s}
.sn-card:hover{border-color:#2A2520}
.sn-avatar{width:44px;height:44px;border-radius:50%;background:var(--fm-bg3,#1E1E1E)!important;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#BFA164!important;flex-shrink:0;overflow:hidden}
.sn-avatar img{width:100%;height:100%;object-fit:cover}
.sn-trust{padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;text-transform:capitalize}
.tl-new{background:rgba(107,99,86,.2)!important;color:var(--fm-m,#A09888)!important}
.tl-trusted{background:rgba(241,180,76,.12)!important;color:#f1b44c!important}
.tl-verified{background:rgba(80,200,120,.12)!important;color:#50C878!important}
.tl-premium{background:rgba(191,161,100,.15)!important;color:#BFA164!important}
.sn-label{font-size:12px;font-weight:600;color:#D4C9B8!important;margin-bottom:4px;display:block}
.sn-modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.7)!important;z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px}
.sn-modal{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#2A2520);border-radius:12px;padding:24px;width:100%;max-width:400px}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .sn-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .sn-card { padding: 12px !important; border-radius: 10px !important; }
  .sn-label { font-size: 11px; }
  .sn-modal-overlay { padding: 12px; align-items: flex-end; }
  .sn-modal { padding: 18px; border-radius: 14px 14px 0 0; }
}
</style>
<script>
import { executeJson, flocker, uploads } from "@/api";
export default {
  name: "SupplierNetworkPage",
  data(){return{
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
loading:true,suppliers:[],searchQuery:'',trustFilter:'',showAddModal:false,addForm:{supplierId:'',nickname:'',trustLevel:'new'}}},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    filtered(){
      var self=this;var list=this.suppliers;
      if(this.trustFilter)list=list.filter(function(s){return s.trust_level===self.trustFilter});
      if(this.searchQuery.trim()){var q=this.searchQuery.toLowerCase();list=list.filter(function(s){return(s.supplier_name||'').toLowerCase().indexOf(q)>=0||(s.nickname||'').toLowerCase().indexOf(q)>=0||(s.county_name||'').toLowerCase().indexOf(q)>=0})}
      return list
    }
  },
  mounted(){
    // Apply global theme
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
var u=sessionStorage.getItem('userId');if(!u){this.$router.push('/login');return}this.fetch()},
  methods:{
    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},
    async fetch(){this.loading=true;try{var r=await executeJson({function:"getMySuppliers",aggregatorId:sessionStorage.getItem('userId')},flocker);if(r.data.success)this.suppliers=r.data.data.suppliers||[]}catch(e){console.error(e)}this.loading=false},
    async addSupplier(){
      try{var r=await executeJson({function:"manageSupplierNetwork",aggregatorId:sessionStorage.getItem('userId'),supplierId:this.addForm.supplierId,action:'add',trustLevel:this.addForm.trustLevel,nickname:this.addForm.nickname},flocker);
      if(r.data.success){this.showAddModal=false;this.addForm={supplierId:'',nickname:'',trustLevel:'new'};this.fetch()}}catch(e){console.error(e)}
    },
    editSupplier(s){var level=prompt('Trust level (new/trusted/verified/premium):',s.trust_level);if(level){var nick=prompt('Nickname:',s.nickname||s.supplier_name)||'';executeJson({function:"manageSupplierNetwork",aggregatorId:sessionStorage.getItem('userId'),supplierId:s.supplier_id,action:'update',trustLevel:level,nickname:nick},flocker).then(function(){s.trust_level=level;if(nick)s.nickname=nick})}},
    async removeSupplier(s){if(!confirm('Remove '+( s.nickname||s.supplier_name)+' from your network?'))return;try{var r=await executeJson({function:"manageSupplierNetwork",aggregatorId:sessionStorage.getItem('userId'),supplierId:s.supplier_id,action:'remove'},flocker);if(r.data.success)this.suppliers=this.suppliers.filter(function(x){return x.supplier_id!==s.supplier_id})}catch(e){console.error(e)}},
    callSupplier(s){window.location.href='tel:'+s.supplier_phone},
    countTrust(level){return this.suppliers.filter(function(s){return s.trust_level===level}).length},
    formatPrice(a){return a?Number(a).toLocaleString('en-KE'):'0'},
    getInitials(n){if(!n)return'?';return n.split(' ').map(function(w){return w[0]}).join('').substring(0,2).toUpperCase()}
  }
};
</script>
